// @ts-nocheck
import React, {FC, useState, useRef, useEffect, useMemo} from 'react';
import { whichAuth as getAuth } from '../../../getAuth';
import { userState, invoiceToApproveAdminState } from '../../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { handleErrors } from '../../../utils/handleErrors';
import {
  IonModal,
  IonCard,
  IonCardTitle,
  IonBadge,
  IonCardSubtitle,
  IonCardContent,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonHeader,
  IonText,
  IonImg,
  IonButton,
  IonLabel,
  IonInput,
  IonSpinner,
  IonAlert
} from '@ionic/react';
import { AlertModal, ProviderSearch } from "../../../components";
import cn from "classnames";
import { AdminBillApprovalProps, FieldProps } from "./AdminBillApproval.model";
import { REST_API_URL } from "../../../constants/global";
import ArrowLeftBackIcon from '../../../assets/arrow-left-back.svg'

const AdminBillApproval: FC<AdminBillApprovalProps> = ({ setData, payInvoice, approveInvoice, markHistorical, deleteInvoice }) => {
  const user = useRecoilValue(userState);
  const invoiceToApprove = useRecoilValue(invoiceToApproveAdminState);
  const setInvoiceToApprove = useSetRecoilState(invoiceToApproveAdminState);

  const [showProviderSearch, setShowProviderSearch] = useState<boolean>(false);
  const [inputFieldsDisabled, setInputFieldsDisabled] = useState<boolean>(false);
  const [invoiceUser, setInvoiceUser] = useState<any | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalMessage, setModalMessage] = useState<string>('');
  const [amount, setAmount] = useState<number>(0)
  const [issueDate, setIssueDate] = useState<string>('')
  const [dueDate, setDueDate] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [receiptId, setReceiptId] = useState<string>('')
  const [classError, setClassError] = useState<FieldProps[]>([])

  const formatValue = (value, field) => {
    let valueTrimmed = value.trim();
    switch(field) {
      case 'total':
        return valueTrimmed.replaceAll('$', '') * 100;
      default:
        return valueTrimmed;
    }
  }
  
  const getInvoiceUser = () => {
    setInvoiceUser({});
    getAuth()?.currentUser?.getIdToken().then(token =>
      fetch(REST_API_URL + '/users/' + invoiceToApprove?.userIdentity, {
        headers: {'Authorization': 'Bearer ' + token}
      })
      .then(handleErrors)
      .then(invoiceUser => setInvoiceUser(invoiceUser))
      .catch(() => setInvoiceUser({})));
  }

  const updateInvoice = (field: string, value: string) => {
    getAuth()?.currentUser?.getIdToken().then(token => {
      try {
        fetch(REST_API_URL + '/invoices/' + invoiceToApprove?.id, {
          method: 'PATCH',
          body: JSON.stringify({[field]: formatValue(value?._links?.self?.href || value, field)}),
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/merge-patch+json'
          }
        })
        .then(response => {
          if(response.status === 409) {
            setModalTitle("Duplicate bill record");
            setModalMessage("Another bill with the same user, provider and due date already exists.");
            setIsOpenModal(true);
            setClassError([...classError, field])
          } else if(response.status === 200) {
            (FieldProps.Provider === field) && setShowProviderSearch(false);
            setData([field], value, invoiceToApprove?.id)
            setInvoiceToApprove({ ...invoiceToApprove, [field]: value })
            setClassError(classError.filter((item) => item !== field))
          } else {
            if (!classError.includes(field)) {
              setClassError([...classError, field])
            }
          }
        })
        .catch(() => {
          if (!classError.includes(field)) {
            setClassError([...classError, field])
          }
        })
      } catch(error) {
        if (!classError.includes(field)) {
          setClassError([...classError, field])
        }
      }
    })
  }
  
  const dateIsValid = (dateStr: string, field: FieldProps) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateStr?.match(regex) === null) {
      if (!classError.includes(field)) {
        setClassError([...classError, field])
      }
      return false;
    }
    const date = new Date(dateStr);
    const timestamp = date.getTime();
    if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
      if (!classError.includes(field)) {
        setClassError([...classError, field])
      }
      return false;
    }
    setClassError(classError.filter((item) => item !== field))
    return date.toISOString().startsWith(dateStr);
  }

  const submitDisabled = useMemo(() => {
    return !invoiceToApprove?.provider
      || isNaN(amount)
      || !dateIsValid(dueDate, FieldProps.dueDate)
      || !dateIsValid(endDate, FieldProps.endDate)
      || !dateIsValid(startDate, FieldProps.startDate)
      || !(new Date(startDate) < new Date(endDate))
      || !(new Date(endDate) < new Date(dueDate))
  }, [amount, dueDate, endDate, startDate, invoiceToApprove?.provider])

  const getInvoiceUrl = () => getAuth().currentUser.getIdToken().then(token =>
    fetch(user._links.invoices.href + '/'+ invoiceToApprove?.id, {headers: {'Authorization': 'Bearer ' + token}})
      .then(handleErrors)
      .catch(() => {
        setIsOpenModal(true)
        setModalMessage('Failed to show bill PDF.')
      }));
      
  const openInvoice = () => {
    setDownloadingInvoice(true);
    getInvoiceUrl()
      .then(url => window.open(url, '_blank'))
      .finally(() => setDownloadingInvoice(false));
  }
  const approve = async () => {
    approveInvoice(invoiceToApprove?.id);
    closeModal();
  }
  
  const historical = async () => {
    markHistorical(invoiceToApprove?.id);
    closeModal();
  }
  
  const markInvoiceAsPaid = async () => {
    payInvoice(invoiceToApprove?.id);
    closeModal();
  }
  
  const closeModal = async () => {
    setInvoiceToApprove(null);
    setInvoiceUrl(null);
  }
  
  const showInvoice = () => !!invoiceUrl && 
    <div className="invoice-iframe-div">
      <iframe src={invoiceUrl} frameBorder='0' className="invoice-iframe" title="bill" />
    </div>
  
  useEffect(() => {
    if (!!invoiceToApprove && !invoiceUrl) {
      getInvoiceUrl().then(url => setInvoiceUrl(url))
    }
  }, [invoiceToApprove]);

  useEffect(() => {
    if (!!invoiceToApprove) {
      setAmount(invoiceToApprove?.total)
      setDueDate(invoiceToApprove?.dueDate)
      setStartDate(invoiceToApprove?.startDate)
      setEndDate(invoiceToApprove?.endDate)
      setIssueDate(invoiceToApprove?.issueDate)
      setReceiptId(invoiceToApprove?.receiptId)
      setClassError([])
    }
  }, [invoiceToApprove]);
  
  useEffect(() => {
    if(!!invoiceToApprove) {
      getInvoiceUser();
      setInputFieldsDisabled(!['UNVERIFIED','QUERY'].includes(invoiceToApprove.status));
    }}, [invoiceToApprove?.id]);

  return (
    <IonModal
      isOpen={!!invoiceToApprove}
      className="big-modal"
      onDidDismiss={closeModal}
    >
      <IonHeader className="invoice-header">
        <IonIcon icon={ArrowLeftBackIcon} className="invoice-header-icon" onClick={closeModal} />
        <IonText className="invoice-header-text">Billing information</IonText>
      </IonHeader>
      <IonGrid className="invoice-container">
        <IonRow className="invoice-container-row">
          <IonCol className="invoice-container-col left admin">
            <IonCol size="12" className="settings-col">
              <IonCard>
                <IonCardContent>
                <IonCardTitle>
                  {invoiceUser?.name}
                  <IonBadge style={{float:'right'}}>{invoiceUser?.paymentOption}</IonBadge>
                  <IonBadge style={{float:'right'}} color={invoiceUser?.lastPaymentSuccessful ? 'success' : 'danger'}>{invoiceUser?.id}</IonBadge>
                </IonCardTitle>
                <IonCardSubtitle>{invoiceUser?.address}</IonCardSubtitle>
                </IonCardContent>
              </IonCard>
            </IonCol>
            {!!invoiceToApprove?.provider?.logo && (
              <IonImg
                src={invoiceToApprove?.provider?.logo.replace(/upload/,'upload/w_200,h_200,c_fit').replace(/.svg$/, '.png')}
                alt="provider_logo"
                className="invoice-icon-header"
              />
            )}
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">Provider</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.Provider) })}
                value={!!invoiceToApprove?.provider && `${invoiceToApprove?.provider?.name} ${invoiceToApprove?.provider?.service}`}
                type="text"
                onClick={() => !inputFieldsDisabled && setShowProviderSearch(true)}
                disabled={true}
                required />
              <IonModal className="provider-modal" 
                isOpen={showProviderSearch}
                onDidDismiss={() => setShowProviderSearch(false)}>
                <div className="provider-modal-content">  
                  <ProviderSearch onSelect={(provider) => {
                    updateInvoice(FieldProps.Provider, provider);
                  }} />
                </div>
              </IonModal>
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">Amount</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.Total) })}
                value={`$${Number(amount).toFixed(2)}`}
                type="text"
                onBlur={(e) => updateInvoice(FieldProps.Total, e.target.value.replace('$', ''))}
                onChange={event => setAmount(event)}
                disabled={inputFieldsDisabled}
                required
              />
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">Issued</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.IssueDate) })}
                value={issueDate}
                type="text"
                onBlur={(e) => {
                  if (dateIsValid(e?.target?.value, FieldProps.IssueDate)) {
                    updateInvoice(FieldProps.IssueDate, e.target.value)
                  }
                }}
                onIonChange={event => setIssueDate(event?.target?.value)}
                disabled={inputFieldsDisabled}
                required
              />
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">Due</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.DueDate) })}
                value={dueDate}
                type="text"
                onBlur={(e) => {
                  if (dateIsValid(e?.target?.value, FieldProps.DueDate)) {
                    updateInvoice(FieldProps.DueDate, e.target.value)
                  }
                }}
                onIonChange={event => setDueDate(event?.target?.value)}
                disabled={inputFieldsDisabled}
                required
              />
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">From</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.StartDate) })}
                value={startDate}
                type="text"
                onBlur={(e) => {
                  if (dateIsValid(e?.target?.value, FieldProps.StartDate)) {
                    updateInvoice(FieldProps.StartDate, e.target.value)
                  }
                }}
                onIonChange={event => setStartDate(event?.target?.value)}
                disabled={inputFieldsDisabled}
                required
              />
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">To</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.EndDate) })}
                value={endDate}
                type="text"
                onBlur={(e) => {
                  if (dateIsValid(e?.target?.value, FieldProps.EndDate)) {
                    updateInvoice(FieldProps.EndDate, e.target.value)
                  }
                }}
                onIonChange={event => setEndDate(event?.target?.value)}
                disabled={inputFieldsDisabled}
                required
              />
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">Status</IonLabel>
              <IonInput
                className="settings-content-input"
                value={invoiceToApprove?.status}  
                type="text"
                disabled={true}
              />
            </IonCol>
            <IonCol size="12" className="settings-col">
              <IonLabel className="settings-content-label">Bill</IonLabel>
              <IonInput
                className={cn('settings-content-input', { 'error': classError.includes(FieldProps.ReceiptId) })}
                value={receiptId}
                type="text"
                onBlur={(e) => updateInvoice(FieldProps.ReceiptId, e.target.value)}
                onChange={(event) => setReceiptId(event)}
                disabled={inputFieldsDisabled}
                required
              />
            </IonCol>
            <IonCol size="12" className="invoice-container-col-card buttons no-border no-padding">
              { invoiceUser?.paymentOption !== 'MANUAL' && 
                <>
                { invoiceToApprove?.status === 'PENDING_PAYMENT' &&
                  <IonButton className="invoice-button" color="primary"
                    onClick={markInvoiceAsPaid}>
                    {invoiceUser?.paymentOption === 'AUTO' ? 'Pay' : 'Paid'}
                  </IonButton>
                }
                
                { ['UNVERIFIED', 'QUERY'].includes(invoiceToApprove?.status) && invoiceToApprove?.provider?.service !== 'Fuel' &&
                  <IonButton className="invoice-button"
                    disabled={submitDisabled}
                    onClick={approve} >
                    Approve & Next
                  </IonButton>
                }
                
                { ['UNVERIFIED'].includes(invoiceToApprove?.status) && invoiceToApprove?.provider?.service === 'Fuel' &&
                  <IonButton className="invoice-button"
                    disabled={submitDisabled}
                    onClick={markInvoiceAsPaid} >
                    Paid
                  </IonButton>
                }
                </>
              }
              
              <IonButton onClick={openInvoice} className="invoice-button invoice-download-button">
                {downloadingInvoice && <IonSpinner name="dots" />}
                Download
              </IonButton>
                
              { invoiceToApprove?.status === 'UNVERIFIED' &&
                <IonButton className="invoice-button" color="light"
                  disabled={submitDisabled}
                  onClick={historical} >
                  Historical
                </IonButton>
              }
              
              { ['UNVERIFIED', 'PENDING_PAYMENT'].includes(invoiceToApprove?.status) &&
                <IonButton className="invoice-button" color="danger"
                  onClick={() => setShowDeleteAlert(true)}>
                  Delete
                </IonButton>
              }
              
              <IonButton className="invoice-button" color="medium"
                onClick={closeModal} >
                Close
              </IonButton>
            </IonCol>
          </IonCol>
          
          <IonCol className="invoice-iframe-col invoice-container-col right">
            { showInvoice() }
            </IonCol>
        </IonRow>
        
        <AlertModal
          isOpen={isOpenModal}
          trigger="errorCard"
          title={modalTitle}
          subTitle={modalMessage}
          onClosed={() => {
            setIsOpenModal(false);
            setModalTitle('');
            setModalMessage('');
          }}
          hasButton
        />
        
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Delete invoice?"
          buttons={[
            {
              text: 'Delete',
              handler: () => {
                deleteInvoice(invoiceToApprove?.id);
                closeModal();
              }
            },
            {
              text: 'Cancel',
              role: 'cancel'
            }
          ]}
        />
      </IonGrid>
    </IonModal>
  );
}

export default AdminBillApproval;
